import { Code, ConnectError } from '@bufbuild/connect';
import { Timestamp } from '@bufbuild/protobuf';
import {
  clearSessionCorrelation,
  setSessionCorrelation,
} from '@event-tracking';
import {
  parseIdParam,
  ActionsPopup,
  ContentIdAttempt,
  GeneoIotProjectFile,
  GeneoIotProjectMeta,
  IStyles,
  IconWrapper,
  ImageWrapper,
  InPageHeader,
  Loader,
  NoContentCard,
  PaginationButton,
  ResourceListDropdown,
  ResourcePageContent,
  deepClone,
  deserify,
  getMediaBasePath,
  practicalProjectToProjectFile,
  pxToRem,
  pxTovW,
  resourceTypeName,
  shouldIncludeCredentialsInRequest,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import {
  Content,
  PageContentInfo,
  PracticalProject,
  PracticalProjectMeta,
  PracticalWorkspace,
  Question,
  ResourceCategoryEnum,
  ResourceContent,
  ResourceTeacherInstruction,
  Resource_ResourceEnum,
} from '@protos/content_management/content.db_pb';
import {
  UpdateStudentProjectResourceSessionRequest,
  UpdateStudentProjectSessionRequest,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import {
  ContentAttempt,
  SessionModeEnum,
  SessionStatusEnum,
  StudentContentResponse,
  StudentProjectSessionMetrics,
  TaskStudentAttemptStatusEnum,
  TeacherContentResponse,
  VisitedResourceContentInfoList,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { HOME } from '../../../routeHandling/RoutesNomenclature';
import {
  navigateBackToProjectDetails,
  navigateToProjectResource,
  projectResourceUrl,
} from '../utils';
import {
  ResourceSessionOwner,
  setPreviousProjectSessionInfo,
  setResourceRedirectionPath,
  setSelectedResourceContent,
  setSelectedResourceInfo,
  setSelectedResourceSessionId,
  setUpdatedLessonSessionVisitedResourceInfo,
} from '../reducer/labaroatory.slice';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../Auth/Login/login_logout.events';
import {
  aiContentCloseEvent,
  aiContentOpenEvent,
  aiContentQuestionSubmitEvent,
  aiResourceCloseEvent,
  aiResourceOpenEvent,
  aiResourcePageCloseEvent,
  aiResourcePageOpenEvent,
} from '../../Learn/utils_learn/learn.events';
// Feature removed: content-lock not required for ProjectResource
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  useAllowResourceDownload,
  useProjectAssessmentEnabled,
} from '../../../utils/customHooks';
// LmsAssessmentCommonAPIServiceV1Client not used here

const styles: IStyles = {
  paginationButtonBox: {
    position: 'fixed',
    bottom: { md: pxToRem(24), lg: pxTovW(30), xs: pxToRem(10) },
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  iconTextBoxContainer: {
    width: '100%',
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    alignItems: 'center',
    mt: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
  },
  headingImage: {
    width: { xs: pxToRem(60), md: pxToRem(105) },
    height: { xs: pxToRem(60), md: pxToRem(105) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    objectFit: 'cover',
  },
  resourcePage: {
    paddingBottom: {
      xs: pxToRem(75),
      md: pxToRem(72),
      lg: pxTovW(110),
    },
  },
};

/**
 * The Geneo IoT editor's file format is its own; this maps it onto the proto the
 * LMS stores. `savedAt` is the frame's clock, so it is only trusted as far as
 * `Date` can parse it - an unparseable value falls back to now rather than
 * building an invalid Timestamp, which would fail the whole write.
 */
const toPracticalResponse = (
  project: GeneoIotProjectFile,
  identity?: GeneoIotProjectMeta
): PracticalProject => {
  const savedAt = new Date(project.savedAt);
  const meta = identity ?? project.meta;
  return new PracticalProject({
    board: project.board,
    editorMode: project.editorMode,
    meta: new PracticalProjectMeta({
      name: meta?.name,
      author: meta?.author,
      description: meta?.description,
    }),
    savedAt: isNaN(savedAt.getTime())
      ? Timestamp.fromDate(new Date())
      : Timestamp.fromDate(savedAt),
    version: project.version,
    workspace: new PracticalWorkspace({
      generatedCode: project.workspace?.generatedCode,
      xml: project.workspace?.xml,
    }),
  });
};

interface IProps {
  headerButtonText?: string;
  buttonClickHandler?: () => void;
}
export default function ProjectResource(props: IProps) {
  const navigate = useNavigate();
  const userInfo = deserify(useAppSelector((state) => state.auth.userInfo));
  const studentId = userInfo?.studentProfileId;
  const activeResourceContent = deserify(
    useAppSelector((state) => state.laboratory.selected_resource_content)
  )?.parsedData;
  const rawResourceContent = deserify(
    useAppSelector((state) => state.laboratory.selected_resource_content)
  )?.rawData;
  const persistedResourceSessionId = deserify(
    useAppSelector((state) => state.laboratory.selected_resource_session_id)
  );
  const persistedResourceSessionOwner = deserify(
    useAppSelector((state) => state.laboratory.selected_resource_session_owner)
  );
  const [standaloneResourceSessionId, setStandaloneResourceSessionId] =
    useState<bigint>();
  const selectedLessonContent = deserify(
    useAppSelector((state) => state.laboratory.selected_lesson_content)
  );
  const {
    ContentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV1ClientWithStatusCodeHandler,
    LessonLearnAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { isOffline } = useDownloadContext();

  const [completedResourceIds, setCompletedResourceIds] = useState<string[]>(
    []
  );

  //~ Practical resources render the Geneo IoT editor instead of page contents.
  //~ Nothing is persisted on the editor's side, so the project file it pushes
  //~ on a save is held here and sent with the resource session update.
  const practicalProjectRef = useRef<GeneoIotProjectFile | undefined>(
    undefined
  );
  const practicalSavePromiseRef = useRef<Promise<void> | undefined>(undefined);
  const practicalSavePendingRef = useRef(false);
  //~ Set once a save has marked this resource complete. A ref because a
  //~ queued save re-runs from an older render, before the state has caught up.
  const practicalMarkedCompleteRef = useRef(false);
  //~ True while the editor holds work the student has not saved inside it.
  //~ A ref because the header's exit handlers close over stale state.
  const practicalDirtyRef = useRef(false);
  //~ An exit or Next waiting on the student to confirm losing unsaved work
  const [pendingLeave, setPendingLeave] = useState<() => void>();
  //~ A teammate saved newer code, so this student's last save was rejected.
  //~ Nothing reloads on its own - the student copies what they need and
  //~ presses Reload. A ref too, for the header's stale exit handlers.
  const [codeConflict, setCodeConflict] = useState(false);
  const codeConflictRef = useRef(false);
  const [confirmReload, setConfirmReload] = useState(false);
  //~ Bumped to remount the editor, which only reads its project on open
  const [editorReloadKey, setEditorReloadKey] = useState(0);
  //~ The group submitted, or the project ended with no late submission. The
  //~ page stays open read-only, with no more progress or code writes.
  const [writesBlocked, setWritesBlocked] = useState(false);
  const writesBlockedRef = useRef(false);
  //~ The group's last save for this resource, if there is one. Resolved before
  //~ the page leaves its loading state, because the frame reads the project it
  //~ opens with once, during the handshake - it is not a live prop.
  const [savedPracticalProject, setSavedPracticalProject] = useState<
    GeneoIotProjectFile | undefined
  >(undefined);
  //~ Names the blank project the editor starts with on a first open. The group
  //~ owns the work, so the group - not the student - is its author.
  const [practicalGroupName, setPracticalGroupName] = useState<
    string | undefined
  >(undefined);
  const [isProjectSubmitted, setIsProjectSubmitted] = useState(false);
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();

  const dispatch = useDispatch();

  const location = useLocation();
  const pathSegments = location.pathname.split('/');

  const subjectId =
    new URLSearchParams(location.search).get('subjectId') || undefined;
  const resourceId =
    new URLSearchParams(location.search).get('resourceId') || undefined;
  const lessonId =
    new URLSearchParams(location.search).get('lessonId') || undefined;
  const lessonSessionId =
    new URLSearchParams(location.search).get('lessonSessionId') || undefined;
  const chapterId =
    new URLSearchParams(location.search).get('chapterId') || undefined;
  const topicId =
    new URLSearchParams(location.search).get('topicId') || undefined;
  const assessmentId =
    new URLSearchParams(location.search).get('assessmentId') || undefined;
  const projectId =
    new URLSearchParams(location.search).get('projectId') || undefined;
  const effectiveAssessmentId = assessmentId || projectId;
  // int64 ids for requests; undefined when the URL value isn't a valid id.
  const assessmentIdValue = parseIdParam(effectiveAssessmentId);
  const lessonSessionIdValue = parseIdParam(lessonSessionId);

  const demoResource =
    new URLSearchParams(location.search).get('demoResource') || undefined;
  const sessionModeParam =
    new URLSearchParams(location.search).get('sessionMode') || undefined;
  const sessionMode = sessionModeParam
    ? (Number(sessionModeParam) as SessionModeEnum)
    : undefined;
  //~ Opened to review work that is already stored - from a report - rather
  //~ than to do the project. Nothing here may create a session or write back:
  //~ the reviewer is not attempting the resource, and on a group project the
  //~ stored practical belongs to the team. Mirrors `viewMode` on the teacher's
  //~ ResourcePage.
  const isReviewMode =
    new URLSearchParams(location.search).get('viewMode') === 'view';
  //~ Opened in its own tab from the resource list, so there is no project
  //~ sequence to page through
  const isStandalone =
    new URLSearchParams(location.search).get('standalone') === 'true';
  //~ The store is persisted, so a new tab rehydrates the opening tab's resource
  //~ session. A standalone tab keeps its own session in local state instead,
  //~ so it neither writes into that session nor overwrites it for a reload.
  //~ The persisted id survives logout and unclean exits, so it is only this
  //~ visit's session if it was opened by this student, for this resource, in
  //~ this project session. Anything else would write onto someone else's (or
  //~ another resource's) session.
  const resourceSessionOwner: ResourceSessionOwner | undefined =
    studentId !== undefined && resourceId && lessonSessionId
      ? {
          studentId: String(studentId),
          resourceId,
          projectSessionId: lessonSessionId,
        }
      : undefined;
  const isPersistedSessionOwned =
    !!resourceSessionOwner &&
    persistedResourceSessionOwner?.studentId ===
      resourceSessionOwner.studentId &&
    persistedResourceSessionOwner?.resourceId ===
      resourceSessionOwner.resourceId &&
    persistedResourceSessionOwner?.projectSessionId ===
      resourceSessionOwner.projectSessionId;
  const activeResourceSessionId = isStandalone
    ? standaloneResourceSessionId
    : isPersistedSessionOwned
    ? persistedResourceSessionId
    : undefined;
  const setActiveResourceSessionId = (sessionId?: bigint) => {
    if (isStandalone) {
      setStandaloneResourceSessionId(sessionId);
    } else {
      dispatch(
        setSelectedResourceSessionId({
          sessionId,
          owner: resourceSessionOwner,
        })
      );
    }
  };

  const isProjectAssessmentEnabled = useProjectAssessmentEnabled();

  const allowResourceDownload = useAllowResourceDownload(
    subjectId && demoResource !== 'true' ? Number(subjectId) : undefined
  );

  const lesson_session_visited_resource_info =
    deserify(
      useAppSelector(
        (state) => state.laboratory.lesson_session_visited_resource_info
      )
    ) || {};
  const visitedResourceInfo =
    (lessonSessionId
      ? lesson_session_visited_resource_info[lessonSessionId]
      : []) || [];
  const resourceRedirectionPath = useAppSelector(
    (state) => state.laboratory.resource_redirection_path
  );
  const pageContents = (
    new ResourceContent(activeResourceContent)?.resourceContentData
      ?.pageContent || []
  ).sort((a, b) => a.pageNumber - b.pageNumber);
  const [pageNumber, setPageNumber] = useState(0);
  const [contentIdAttempt, updateContentIdAttempt] = useState<ContentIdAttempt>(
    {}
  );

  const pageContent = pageContents[pageNumber];
  const isPracticalResource =
    rawResourceContent?.resourceType ===
    Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL;
  //~ Identity for the blank project the editor creates on a first open. The
  //~ resource id is what the saved work is keyed by, so it is the project's
  //~ name; the group owns the work, so it is the author; and the resource's
  //~ own title is the brief, so it is the description.
  const practicalMeta: GeneoIotProjectMeta | undefined =
    isPracticalResource && resourceId
      ? {
          name: resourceId,
          author: practicalGroupName || '',
          description: rawResourceContent?.title || '',
        }
      : undefined;
  //~ The identity every save is stored under: the one the resumed work already
  //~ carries, or the one the blank project was named with. Held on the host so
  //~ nothing done inside the editor - importing the sample above all - can
  //~ rewrite whose work this is.
  const practicalIdentity = savedPracticalProject?.meta ?? practicalMeta;
  const [startTime, setStartTime] = useState(Timestamp.fromDate(new Date()));
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  //~ Holds the page while Next or an exit saves. Unlike a 'loading' render it
  //~ keeps the IoT frame mounted, so a save the student just made inside the
  //~ editor can still land before moving on.
  //~ The ref guards the header's exit handlers, which close over stale state.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const setSubmitting = (submitting: boolean) => {
    isSubmittingRef.current = submitting;
    setIsSubmitting(submitting);
  };

  const {
    selected_resource_info,
    active_homework_content,
    previous_project_session_info,
  } = deserify(useAppSelector((state) => state.laboratory));
  //~ The group version guards practical code only. It changes here on load,
  //~ after this student's own code save, and on Reload - never in the
  //~ background, so a teammate's save can't swap the editor's base unnoticed.
  const groupVersionRef = useRef<bigint | undefined>(
    previous_project_session_info?.groupVersion
  );
  //~ Project resources come from the project content, lesson content is only a fallback
  const projectResources =
    (effectiveAssessmentId && active_homework_content
      ? active_homework_content[effectiveAssessmentId]?.projectResources
      : undefined) || [];
  const projectResourceIds = projectResources.length
    ? projectResources.map((val) => val.resourceId)
    : selectedLessonContent?.resources.map((val) => val.resourceId) || [];
  //~ The project's other resources, offered as links so the student can open
  //~ one without losing the practical they are working in.
  const projectResourceLinks = projectResources.map((resource) => ({
    resourceId: resource.resourceId,
    title: resource.title || resource.resourceId,
    subtitle: resourceTypeName(resource.resourceType)?.name,
    href: projectResourceUrl({
      resourceId: resource.resourceId,
      projectSessionId: lessonSessionId ? Number(lessonSessionId) : undefined,
      assessmentId: effectiveAssessmentId,
      demoResource: demoResource === 'true',
      sessionMode: sessionMode,
      viewMode: isReviewMode ? 'view' : undefined,
      standalone: true,
    }),
  }));
  useEffect(() => {
    fetchResourceData();
    updateContentIdAttempt({});
    //~ The next resource gets a fresh frame; carrying the previous resource's
    //~ project or its queued save over would store it against the wrong resource
    practicalProjectRef.current = undefined;
    practicalSavePendingRef.current = false;
    practicalMarkedCompleteRef.current = false;
    practicalDirtyRef.current = false;
    codeConflictRef.current = false;
    setCodeConflict(false);
    setSavedPracticalProject(undefined);
    setIsProjectSubmitted(false);
  }, [resourceId]);

  useEffect(() => {
    setSessionCorrelation({
      projectSessionId: lessonSessionId,
      projectResourceSessionId: activeResourceSessionId?.toString(),
    });
    return () =>
      clearSessionCorrelation(['projectSessionId', 'projectResourceSessionId']);
  }, [lessonSessionId, activeResourceSessionId]);

  useEffect(() => {
    //~ Back Arrow
    const handleExit = async (navigateHome: boolean) => {
      if (isReviewMode) {
        if (navigateHome) {
          navigate(HOME);
        } else {
          navigate(-1);
        }
        return;
      }
      //~ Leaving now would unmount the editor under the save already running
      if (isSubmittingRef.current) {
        return;
      }
      //~ Browser back has already left the page when this runs, so there is no
      //~ editor left to ask about - the unsaved work is gone either way.
      const hasLeftPage =
        window.location.pathname + window.location.search !==
        location.pathname + location.search;
      if (
        (practicalDirtyRef.current || codeConflictRef.current) &&
        !hasLeftPage
      ) {
        setPendingLeave(() => () => exitResource(navigateHome));
        return;
      }
      await exitResource(navigateHome);
    };
    setSelectedFunction(() => handleExit);
    setSelectedLogoClickHandler(() => () => handleExit(true));
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
    //~ `pendingLeave` re-registers the handlers once the popup closes, because
    //~ the header drops its back handler after calling it.
  }, [
    activeResourceSessionId,
    resourceId,
    contentIdAttempt,
    pageNumber,
    completedResourceIds,
    pendingLeave,
  ]);

  const exitResource = async (navigateHome: boolean) => {
    const hasLeft = await handleResourceCompleteOrEnd(
      SessionStatusEnum.SESSION_STATUS_EXITED
    );
    // dispatch(setSelectedResourceSessionId(undefined));

    //~ Not when the exit was held back to keep code a teammate's save rejected
    if (hasLeft && navigateHome) {
      navigate(HOME);
    }
  };

  const leaveWithoutSaving = () => {
    const leave = pendingLeave;
    setPendingLeave(undefined);
    leave?.();
  };

  //~ Sorts a rejected write. A code conflict keeps the student on the page with
  //~ their code intact; a submitted or ended project turns the page read-only;
  //~ anything else is a plain failure that must not trap them.
  const handleWriteError = (
    error: unknown,
    failureLabel: string
  ): 'conflict' | 'closed' | 'failed' => {
    console.error(error);
    const connectError = ConnectError.from(error);
    if (connectError.code === Code.Aborted) {
      codeConflictRef.current = true;
      setCodeConflict(true);
      dispatch(
        setToastInfo({
          label:
            'A teammate saved newer code. Your latest changes are not saved.',
          variant: 'error',
          open: true,
        })
      );
      return 'conflict';
    }
    const isClosed =
      connectError.code === Code.DeadlineExceeded ||
      (connectError.code === Code.FailedPrecondition &&
        /already (been )?submitted/i.test(connectError.rawMessage));
    if (isClosed) {
      writesBlockedRef.current = true;
      setWritesBlocked(true);
      dispatch(
        setToastInfo({
          label: connectError.rawMessage,
          variant: 'error',
          open: true,
        })
      );
      return 'closed';
    }
    dispatch(
      setToastInfo({ label: failureLabel, variant: 'error', open: true })
    );
    return 'failed';
  };

  //~ The student's deliberate way back in sync: the group's latest code and
  //~ version, with the editor reopened on it.
  const reloadPracticalProject = async () => {
    setConfirmReload(false);
    if (!resourceId || !assessmentIdValue) {
      return;
    }
    try {
      const prevSessionInfo =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getPreviousProjectSessionInfo(
          { assessmentId: assessmentIdValue }
        );
      const prevSessionData = prevSessionInfo.data;
      dispatch(setPreviousProjectSessionInfo(prevSessionData));
      groupVersionRef.current = prevSessionData?.groupVersion;
      setCompletedResourceIds(prevSessionData?.completedResourceIds ?? []);
      await loadSavedPracticalProject(resourceId, prevSessionData?.groupName);
      practicalProjectRef.current = undefined;
      practicalSavePendingRef.current = false;
      practicalDirtyRef.current = false;
      codeConflictRef.current = false;
      setCodeConflict(false);
      setEditorReloadKey((key) => key + 1);
    } catch (error) {
      console.error(error);
      dispatch(
        setToastInfo({
          label: 'Unable to reload the latest code. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  //~ Teammates' completed resources arrive with any progress response. This
  //~ updates the ticks only - the version is left alone.
  const applyGroupProgress = (groupProgress?: {
    completedResourceIds: string[];
  }) => {
    if (groupProgress?.completedResourceIds) {
      setCompletedResourceIds(groupProgress.completedResourceIds);
    }
  };

  const onReloadClick = () => {
    if (practicalDirtyRef.current || codeConflictRef.current) {
      setConfirmReload(true);
      return;
    }
    reloadPracticalProject();
  };

  useEffect(() => {
    setStartTime(Timestamp.fromDate(new Date()));
  }, [pageNumber, resourceId]);

  /**
   * Fetches the group's last save for this practical resource, so the editor
   * opens on it instead of a blank workspace.
   *
   * Always over the network, never from what this page last held: for a
   * collaborative project the stored copy is the GROUP's, so a teammate may
   * have saved since. Resuming from a stale copy and saving would overwrite
   * their work - the server takes the last write and does not gate it on
   * `groupVersion`.
   */
  const loadSavedPracticalProject = async (
    practicalResourceId: string,
    fallbackGroupName?: string
  ) => {
    if (demoResource === 'true' || !effectiveAssessmentId) {
      return;
    }
    try {
      const assessmentResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
          {
            studentId: studentId,
            assessmentId: assessmentIdValue,
          }
        );
      const data = assessmentResponse.data;
      setPracticalGroupName(data?.groupName || fallbackGroupName);
      setIsProjectSubmitted(
        data?.assessmentAttemptStatus ===
          TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED
      );
      //~ Resource-keyed, not ordered - a project with several practicals
      //~ returns several entries and [0] is not necessarily this one
      const saved = data?.practicalResponses.find(
        (entry) => entry.resourceId === practicalResourceId
      );
      setSavedPracticalProject(
        saved?.project
          ? practicalProjectToProjectFile(saved.project)
          : undefined
      );
      //~ For a group project the work loaded may not be the student's own.
      //~ Compared as strings: both are int64, and the response's arrives as one.
      if (
        saved?.project &&
        saved.lastUpdatedByStudentId !== undefined &&
        String(saved.lastUpdatedByStudentId) !== String(studentId)
      ) {
        dispatch(
          setToastInfo({
            label: `Loaded your team's latest saved work`,
            variant: 'info',
            open: true,
          })
        );
      }
    } catch (error) {
      //~ A missing entry is the normal first-open state and does not come back
      //~ as an error, so anything caught here is a real failure. The student
      //~ still gets a blank editor rather than being blocked out of the page.
      console.error(error);
      setSavedPracticalProject(undefined);
      setPracticalGroupName(fallbackGroupName);
    }
  };

  const fetchResourceData = async () => {
    const newPageNumber = 0;
    setPageNumber(newPageNumber);
    if (resourceId) {
      try {
        setLoading('loading');
        const resourceContent =
          await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchResourceContent(
            {
              personId: studentId,
              personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              resourceId: resourceId,
              //~ Scopes the teacher solution to this project - the same practical
              //~ resource carries a different one in every project it is used in.
              assessmentId: assessmentIdValue,
            }
          );

        const isNoContentResponse =
          resourceContent?.status === 1 ||
          resourceContent?.code === '404' ||
          resourceContent?.message?.toLowerCase().includes('no data found');

        if (isNoContentResponse) {
          setLoading('error');
          dispatch(setSelectedResourceContent(undefined));
          setActiveResourceSessionId(undefined);
          dispatch(
            setToastInfo({
              label: 'Resource not found. Going back.',
              variant: 'error',
              open: true,
            })
          );
          navigateBackToProjectDetails(navigate, effectiveAssessmentId);
          return;
        }

        const gcpJsonURL = resourceContent.data?.gcpJsonUrl;
        if (!gcpJsonURL) {
          setLoading('error');
          dispatch(setSelectedResourceContent(undefined));
          setActiveResourceSessionId(undefined);
          dispatch(
            setToastInfo({
              label: 'Error Occured while loading resource content',
              variant: 'error',
              open: true,
            })
          );
          navigateBackToProjectDetails(navigate, effectiveAssessmentId);
          return;
        }
        const modifiedUrl = getMediaBasePath(
          gcpJsonURL,
          resourceContent.data?.resourceCategory ===
            ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
            ? 'userDataBucket'
            : 'resourceContentBucket'
        );
        const options: RequestInit = {};
        if (shouldIncludeCredentialsInRequest(modifiedUrl)) {
          options.credentials = 'include';
        }
        const gcpResponse = await (await fetch(modifiedUrl, options)).json();
        const resContent = new ResourceContent().fromJson(gcpResponse, {
          ignoreUnknownFields: true,
        });
        dispatch(
          setSelectedResourceContent({
            rawData: resourceContent.data,
            parsedData: resContent,
          })
        );

        if (demoResource !== 'true' && !isReviewMode) {
          // Check if activeResourceSessionId already exists for the current resourceId
          if (!activeResourceSessionId) {
            const resourceSession =
              await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.createStudentProjectResourceSession(
                {
                  startTime: Timestamp.fromDate(new Date()),
                  resourceId: resourceId,
                  // schoolId: userInfo?.schoolDetails?.schoolId,
                  // academicYear: 0,
                  // classId: userInfo?.classSectionDetails?.classId,
                  // section: userInfo?.classSectionDetails?.sectionName,
                  // subject:
                  //   subjectId && !isNaN(Number(subjectId))
                  //     ? getStudentSubjectEnum(
                  //       Number(subjectId),
                  //       userInfo?.learnSubjects
                  //     )
                  //     : undefined,
                  // moduleId: topicId
                  //   ? Number(topicId)
                  //   : chapterId
                  //     ? Number(chapterId)
                  //     : undefined,
                  studentProjectSessionId: lessonSessionIdValue,
                  // sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
                }
              );
            setActiveResourceSessionId(
              resourceSession.studentProjectResourceSessionId
            );
          }
        }

        dispatch(
          setSelectedResourceInfo({
            pageNumber: newPageNumber + 1,
            resourceId: resourceId,
            pageContent: (
              resContent?.resourceContentData?.pageContent || []
            ).map((val) => new PageContentInfo(val)),
            resourceResponse: gcpResponse,
            currentAnswer: [],
          })
        );

        if (effectiveAssessmentId) {
          const prevLessonSessionInfo =
            await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getPreviousProjectSessionInfo(
              {
                assessmentId: assessmentIdValue,
              }
            );
          const prevSessionData = prevLessonSessionInfo.data;
          dispatch(setPreviousProjectSessionInfo(prevSessionData));
          groupVersionRef.current = prevSessionData?.groupVersion;
          setPracticalGroupName(prevSessionData?.groupName);
          const resIds = prevSessionData?.completedResourceIds;
          if (resIds && resIds.length > 0) {
            setCompletedResourceIds(resIds);
          } else {
            setCompletedResourceIds([]);
          }
          //~ Only a practical resource has an editor to fill, and the saved
          //~ work only exists on a project task
          if (
            resourceContent.data?.resourceType ===
            Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL
          ) {
            await loadSavedPracticalProject(
              resourceId,
              prevSessionData?.groupName
            );
          }
        } else {
          setCompletedResourceIds([]);
          setPracticalGroupName(undefined);
        }
        setLoading('completed');
      } catch (err) {
        console.error(err);
        setLoading('error');
        dispatch(setSelectedResourceContent(undefined));
        setCompletedResourceIds([]);
        setActiveResourceSessionId(undefined);
      }
    }
  };

  const makeProjectSessionObject = (
    resourceStatus: SessionStatusEnum
  ): UpdateStudentProjectSessionRequest => {
    const resourceIdsCompletedCopy = completedResourceIds.slice();
    let projectStatus = resourceStatus;
    if (
      resourceStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED &&
      resourceId
    ) {
      if (!resourceIdsCompletedCopy.includes(resourceId)) {
        resourceIdsCompletedCopy.push(resourceId);
        setCompletedResourceIds(resourceIdsCompletedCopy);
      }
      const resourceIdsOfProject = projectResourceIds;
      const notCompletedResources = resourceIdsOfProject.filter(
        (val) => !resourceIdsCompletedCopy.includes(val)
      );
      projectStatus =
        notCompletedResources.length > 0 &&
        resourceStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          ? SessionStatusEnum.SESSION_STATUS_ONGOING
          : projectStatus;
    }
    const pageNo = pageContent.pageNumber;
    const conIds = pageContent.contents.map((con) => {
      const contentValue = con.model.value;
      const contentId =
        contentValue instanceof Content
          ? contentValue.contentId
          : contentValue instanceof Question
          ? contentValue.questionId
          : contentValue instanceof ResourceTeacherInstruction
          ? contentValue.resourceTeacherInstructionId
          : undefined;
      return contentId || '';
    });
    const updatedResourceInfo = resourceId
      ? getUpdatedVisitedResourceInfo(resourceId, pageNo, conIds)
      : visitedResourceInfo;
    if (lessonSessionId) {
      dispatch(
        setUpdatedLessonSessionVisitedResourceInfo({
          ...lesson_session_visited_resource_info,
          [lessonSessionId]: updatedResourceInfo,
        })
      );
    }
    return new UpdateStudentProjectSessionRequest({
      studentProjectSessionId: lessonSessionIdValue,
      assessmentId: assessmentIdValue,
      baseGroupVersion: groupVersionRef.current,
      sessionStatus: projectStatus,
      endTime:
        projectStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          ? Timestamp.fromDate(new Date())
          : undefined,
      metrics: new StudentProjectSessionMetrics({
        studentProjectSessionId: lessonSessionIdValue,
        isComplete: projectStatus == SessionStatusEnum.SESSION_STATUS_COMPLETED,
        responseScore: 0,
        maxScore: 0,
        completedResourceIds: resourceIdsCompletedCopy,
        visitedResourceContentInfoList: updatedResourceInfo,
      }),
    });
  };

  const handleAnswerSubmit = async (contentId: string, answer: string[]) => {
    dispatch(
      setSelectedResourceInfo({
        ...selected_resource_info,
        currentAnswer: answer,
      })
    );
    if (demoResource !== 'true') {
      await aiContentQuestionSubmitEvent({
        questionId: contentId,
        answer: answer,
        pageNumber: selected_resource_info?.pageNumber,
        pageContent: selected_resource_info?.pageContent || [],
        isOffline: isOffline,
      });
    }

    const contentIdAttemptCopy = deepClone(contentIdAttempt);
    if (!contentIdAttemptCopy[contentId]) {
      contentIdAttemptCopy[contentId] = new TeacherContentResponse({
        contentId: contentId,
        responses: [],
      });
    }
    contentIdAttemptCopy[contentId].responses.push(
      new ContentAttempt({
        startTime: startTime,
        endTime: Timestamp.fromDate(new Date()),
        answer,
      })
    );
    updateContentIdAttempt(contentIdAttemptCopy);
  };

  /**
   * Readies the student's last save for the Next/exit update that follows.
   * Only work saved inside the editor is ever stored - anything unsaved is
   * either confirmed away by the student first or was never there.
   */
  const settlePracticalSaves = async () => {
    if (demoResource === 'true' || isReviewMode || !isPracticalResource) {
      return;
    }
    //~ A queued push-save would land after the status update that follows this
    //~ and revert the session to ONGOING. Drop the queued run - the update
    //~ carries the freshest copy anyway - and let any in-flight one settle, so
    //~ the two cannot race on a last-write-wins server.
    practicalSavePendingRef.current = false;
    await practicalSavePromiseRef.current;
  };

  /**
   * Saving inside the editor is what finishing a practical looks like, so the
   * first save marks the resource complete the way Next does - through the
   * project session's completed resources - while the student stays in the
   * editor to keep working.
   *
   * A standalone tab leaves project progress to the tab the project is worked
   * in, as everywhere else on this page.
   */
  const markPracticalResourceComplete = async () => {
    if (
      !resourceId ||
      !lessonSessionId ||
      !effectiveAssessmentId ||
      isStandalone ||
      writesBlockedRef.current ||
      practicalMarkedCompleteRef.current ||
      completedResourceIds.includes(resourceId)
    ) {
      return;
    }
    const projectSessionResponse =
      await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectSession(
        makeProjectSessionObject(SessionStatusEnum.SESSION_STATUS_COMPLETED)
      );
    practicalMarkedCompleteRef.current = true;
    applyGroupProgress(projectSessionResponse.progress?.groupProgress);
    dispatch(
      setToastInfo({
        label: 'Work saved.',
        variant: 'success',
        open: true,
      })
    );
  };

  /**
   * Persists whatever `practicalProjectRef` currently holds, without disturbing
   * the frame.
   *
   * Deliberately does NOT build the request through
   * `makeProjectResourceSessionObject`: that appends a ContentAttempt per page
   * content and calls `updateContentIdAttempt`, so driving it from a save the
   * student can repeat every few seconds would grow the response list without
   * bound and churn state behind the simulator. Only the practical artifact is
   * sent here; the page-content bookkeeping still rides on the Next/exit saves.
   *
   * `setLoading` is never touched - a 'loading' render replaces the whole page
   * with the loader, which would unmount the iframe and throw away the
   * student's editor session mid-save. Next and exit hold the page with
   * `isSubmitting` for the same reason.
   */
  const savePracticalProject = async (): Promise<void> => {
    //~ Read once: the trailing run below re-reads the ref, so it still sends
    //~ the newest copy rather than replaying this one
    const project = practicalProjectRef.current;
    if (
      demoResource === 'true' ||
      isReviewMode ||
      writesBlockedRef.current ||
      !activeResourceSessionId ||
      !project
    ) {
      return;
    }
    //~ Until the student reloads, every save would be rejected the same way
    if (codeConflictRef.current) {
      dispatch(
        setToastInfo({
          label:
            "Not saved. Press Reload to get your team's latest code first.",
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    //~ The server takes the last write for a resource, so overlapping saves
    //~ could land out of order and leave the older project stored. Queue a
    //~ single trailing run instead - it picks up the latest ref either way.
    if (practicalSavePromiseRef.current) {
      practicalSavePendingRef.current = true;
      return practicalSavePromiseRef.current;
    }
    const run = (async () => {
      try {
        const resourceSessionResponse =
          await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectResourceSession(
            new UpdateStudentProjectResourceSessionRequest({
              studentProjectResourceSessionId: activeResourceSessionId,
              resourceId,
              sessionStatus: SessionStatusEnum.SESSION_STATUS_ONGOING,
              practicalResponse: toPracticalResponse(
                project,
                practicalIdentity
              ),
              baseGroupVersion: groupVersionRef.current,
            })
          );
        //~ This student's own save moved the version
        groupVersionRef.current =
          resourceSessionResponse.groupVersion ?? groupVersionRef.current;

        //~ Only once the work is stored, and still inside this run, so Next and
        //~ exit wait for it too
        try {
          await markPracticalResourceComplete();
        } catch (error) {
          handleWriteError(
            error,
            'Work saved, but unable to mark the resource as completed'
          );
        }
      } catch (error) {
        if (
          handleWriteError(
            error,
            'Error Occurred! Unable to save your work'
          ) === 'conflict'
        ) {
          practicalSavePendingRef.current = false;
        }
      } finally {
        practicalSavePromiseRef.current = undefined;
        if (practicalSavePendingRef.current) {
          practicalSavePendingRef.current = false;
          //~ Awaited so a caller holding this promise waits for the whole chain
          await savePracticalProject();
        }
      }
    })();
    practicalSavePromiseRef.current = run;
    return run;
  };

  const makeProjectResourceSessionObject = (
    sessionStatus: SessionStatusEnum
  ): UpdateStudentProjectResourceSessionRequest => {
    const contentIdAttemptCopy = deepClone(contentIdAttempt);
    pageContent?.contents.forEach((con) => {
      const contentValue = con.model.value;
      const contentId =
        contentValue instanceof Content
          ? contentValue.contentId
          : contentValue instanceof Question
          ? contentValue.questionId
          : contentValue instanceof ResourceTeacherInstruction
          ? contentValue.resourceId
          : undefined;
      if (contentId) {
        if (!contentIdAttemptCopy[contentId]) {
          contentIdAttemptCopy[contentId] = new TeacherContentResponse({
            contentId: contentId,
          });
        }
        if (
          !(contentValue instanceof Question) ||
          contentIdAttemptCopy[contentId].responses.length == 0
        ) {
          contentIdAttemptCopy[contentId].responses.push(
            new ContentAttempt({
              startTime: startTime,
              endTime: Timestamp.fromDate(new Date()),
              answer: undefined,
            })
          );
        }
      }
    });
    updateContentIdAttempt(contentIdAttemptCopy);
    return new UpdateStudentProjectResourceSessionRequest({
      studentProjectResourceSessionId: activeResourceSessionId,
      resourceId,
      sessionStatus: sessionStatus,
      endTime:
        sessionStatus === SessionStatusEnum.SESSION_STATUS_EXITED ||
        sessionStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          ? Timestamp.fromDate(new Date())
          : undefined,
      response: Object.values(contentIdAttemptCopy).map(
        (val) =>
          new StudentContentResponse({
            contentId: val.contentId,
            responses: val.responses,
          })
      ),
      //~ Left off after a conflict or once writes are blocked: it would only be
      //~ rejected again and take the time/status update down with it
      practicalResponse:
        practicalProjectRef.current &&
        !codeConflictRef.current &&
        !writesBlockedRef.current
          ? toPracticalResponse(practicalProjectRef.current, practicalIdentity)
          : undefined,
      baseGroupVersion: groupVersionRef.current,
    });
  };

  function getUpdatedVisitedResourceInfo(
    resourceId: string,
    pageNo: number,
    pageContentIds: string[]
  ) {
    const existingIndex = visitedResourceInfo.findIndex(
      (obj) => obj.resourceId === resourceId && obj.pageNo === pageNo
    );

    if (existingIndex > -1) {
      // If the object with the resourceId and pageNo exists, create a new object with the updated pageContentIds
      const updatedObject = new VisitedResourceContentInfoList({
        ...visitedResourceInfo[existingIndex],
        pageContentIds: Array.from(
          new Set([
            ...visitedResourceInfo[existingIndex].pageContentIds,
            ...pageContentIds,
          ])
        ),
      });
      // Return a new array with the updated object
      return [
        ...visitedResourceInfo.slice(0, existingIndex),
        updatedObject,
        ...visitedResourceInfo.slice(existingIndex + 1),
      ];
    } else {
      // If the object does not exist, return a new array with the new object added
      return [
        ...visitedResourceInfo,
        new VisitedResourceContentInfoList({
          resourceId,
          pageNo,
          pageContentIds,
        }),
      ];
    }
  }

  //~ Resolves false only when the student is kept on the page, so callers that
  //~ navigate on their own afterwards don't take them away from that code.
  const completeOrEndResource = async (
    sessionStatus: SessionStatusEnum
  ): Promise<boolean> => {
    //~ No session was opened, so there is none to close - just go back to
    //~ whatever opened the resource for review.
    if (isReviewMode) {
      navigate(-1);
      return true;
    }
    await settlePracticalSaves();
    //~ A failed save must never trap the student here. The one exception is
    //~ code a teammate's newer save just rejected: they stay, with their code,
    //~ and are asked before leaving it behind.
    if (demoResource !== 'true' && activeResourceSessionId) {
      try {
        const resourceSessionResponse =
          await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectResourceSession(
            makeProjectResourceSessionObject(sessionStatus)
          );
        //~ A practical carried in this update is the student's own save
        groupVersionRef.current =
          resourceSessionResponse.groupVersion ?? groupVersionRef.current;
      } catch (error) {
        if (
          handleWriteError(
            error,
            'Error Occurred! Unable to end the resource'
          ) === 'conflict'
        ) {
          return false;
        }
      }
    }
    if (
      (lessonId && lessonSessionId && topicId && resourceId) ||
      (effectiveAssessmentId && lessonSessionId && resourceId)
    ) {
      const resourceIds = projectResourceIds;
      const currentIndex = resourceIds.indexOf(resourceId);
      const nextResourceId =
        currentIndex >= 0 ? resourceIds[currentIndex + 1] : undefined;
      const isOngoingLessonSession =
        nextResourceId &&
        sessionStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED;
      //~ Project progress is driven by the tab the student is working the
      //~ project in, not by a standalone tab.
      if (
        demoResource !== 'true' &&
        !isStandalone &&
        !writesBlockedRef.current
      ) {
        try {
          const projectSessionResponse =
            await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectSession(
              makeProjectSessionObject(sessionStatus)
            );
          applyGroupProgress(projectSessionResponse.progress?.groupProgress);
        } catch (error) {
          handleWriteError(
            error,
            'Error Occurred! Unable to save your progress'
          );
        }
      }
      setActiveResourceSessionId(undefined);
      if (isOngoingLessonSession) {
        navigateToProjectResource(navigate, {
          resourceId: nextResourceId,
          projectSessionId: Number(lessonSessionId),
          assessmentId: effectiveAssessmentId,
          sessionMode: sessionMode || SessionModeEnum.SESSION_MODE_PROJECT,
        });
      } else {
        navigateBackToProjectDetails(navigate, effectiveAssessmentId);
      }
      // dispatch(setSelectedResourceSessionId(undefined));
      return true;
    }
    if (resourceRedirectionPath) {
      navigate(resourceRedirectionPath);
      dispatch(setResourceRedirectionPath(undefined));
    } else {
      navigateBackToProjectDetails(navigate, effectiveAssessmentId);
    }
    setActiveResourceSessionId(undefined);
    return true;
  };

  const handleResourceCompleteOrEnd = async (
    sessionStatus: SessionStatusEnum
  ): Promise<boolean> => {
    //~ Another exit or Next is already leaving; this one does nothing
    if (isSubmittingRef.current) {
      return false;
    }
    setSubmitting(true);
    try {
      return await completeOrEndResource(sessionStatus);
    } finally {
      setSubmitting(false);
    }
  };

  const onNextClick = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    if (practicalDirtyRef.current || codeConflictRef.current) {
      setPendingLeave(() => submitNextPage);
      return;
    }
    await submitNextPage();
  };

  const submitNextPage = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    setSubmitting(true);
    try {
      await goToNextPage();
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextPage = async () => {
    await interactionEvent({
      url: 'Student_resource_view',
      context: 'project',
      name: 'NEXT',
      lessonSesssionId: lessonSessionId,
      resourceSesssionId: activeResourceSessionId?.toString(),
      isOffline: isOffline,
    });

    if (
      selected_resource_info?.currentAnswer &&
      selected_resource_info?.currentAnswer.length === 0
    ) {
      await aiContentCloseEvent({
        pageNumber: pageNumber + 1,
        pageContent: selected_resource_info?.pageContent,
        answer: selected_resource_info?.currentAnswer,
        isOffline: isOffline,
      });
    }

    await aiResourcePageCloseEvent({
      resourceId: resourceId,
      pageNumber: pageNumber + 1,
      isOffline: isOffline,
    });

    if (pageContents[pageNumber + 1]) {
      //~ A failed progress update should not trap the student on the page
      try {
        await settlePracticalSaves();
        if (demoResource !== 'true' && !isReviewMode) {
          if (activeResourceSessionId) {
            const resourceSessionResponse =
              await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectResourceSession(
                makeProjectResourceSessionObject(
                  SessionStatusEnum.SESSION_STATUS_ONGOING
                )
              );
            //~ A practical carried in this update is the student's own save
            groupVersionRef.current =
              resourceSessionResponse.groupVersion ?? groupVersionRef.current;
          }

          if (lessonSessionId && !writesBlockedRef.current) {
            const projectSessionResponse =
              await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectSession(
                makeProjectSessionObject(
                  SessionStatusEnum.SESSION_STATUS_ONGOING
                )
              );
            applyGroupProgress(projectSessionResponse.progress?.groupProgress);
          }
        }
      } catch (error) {
        //~ Unsaved code is the one thing worth holding the student for
        if (
          handleWriteError(
            error,
            'Error Occurred! Unable to save your progress'
          ) === 'conflict'
        ) {
          return;
        }
      }

      await aiResourcePageOpenEvent({
        resourceId: resourceId,
        pageNumber: pageNumber + 2,
        isOffline: isOffline,
      });

      await aiContentOpenEvent({
        resourceId: resourceId,
        pageNumber: pageNumber + 2,
        pageContent: selected_resource_info?.pageContent,
        isOffline: isOffline,
      });

      dispatch(
        setSelectedResourceInfo({
          ...selected_resource_info,
          pageNumber: pageNumber + 2,
          resourceId: resourceId,
          currentAnswer: [],
        })
      );
      setPageNumber(pageNumber + 1);
    } else {
      await aiResourceCloseEvent({
        resourceId: resourceId,
        isOffline: isOffline,
      });
      await completeOrEndResource(SessionStatusEnum.SESSION_STATUS_COMPLETED);
    }
  };

  const onPreviousClick = async () => {
    if (pageNumber > 0 && pageContents[pageNumber - 1]) {
      dispatch(
        setSelectedResourceInfo({
          ...selected_resource_info,
          pageNumber: pageNumber,
          resourceId: resourceId,
        })
      );
      setPageNumber(pageNumber - 1);
    }
  };

  const renderHeading = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          flexGrow: 1,
          alignItems: 'center',
          padding: { xs: pxToRem(15), lg: pxTovW(0) },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
          }}
        >
          <ImageWrapper
            name="chapterImage"
            type="png"
            styles={styles.headingImage}
            parentFolder="tempAssets"
            path={getMediaBasePath(
              rawResourceContent?.posterImageUrl,
              rawResourceContent?.resourceCategory ===
                ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                ? 'userDataBucket'
                : 'processedMediaBucket'
            )}
          />
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              minWidth: 0,
              flexDirection: 'column',
              gap: pxToRem(10),
            }}
          >
            <Typography
              variant="elementH1"
              sx={{
                width: { xs: '100%', md: pxTovW(1300), lg: pxTovW(1100) },
                wordWrap: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {rawResourceContent?.title || ''}
            </Typography>
            <Box sx={styles.iconTextBoxContainer}>
              {[
                {
                  iconName: 'clock',
                  text: (rawResourceContent?.estimatedTimeInMin || 0) + 'Mins',
                },
                {
                  iconName: resourceTypeName(
                    rawResourceContent?.resourceType ||
                      Resource_ResourceEnum.RESOURCE_TYPE_UNDEFINED
                  )?.icon,
                  text: resourceTypeName(
                    rawResourceContent?.resourceType ||
                      Resource_ResourceEnum.RESOURCE_TYPE_UNDEFINED
                  )?.name,
                },
              ].map((elem, index) => (
                <IconTextBox
                  key={index}
                  iconName={elem.iconName}
                  text={elem.text}
                  index={index}
                />
              ))}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row-reverse' },
            gap: { md: pxToRem(5), lg: pxTovW(5) },
            alignItems: { xs: 'center', md: 'flex-start' },
          }}
        >
          {/* <Button variant="contained" color="secondary">
            Add
          </Button> */}
        </Box>
      </Box>
    );
  };

  if (loading === 'error') {
    return (
      <Box
        width={{ xs: undefined, md: '95%' }}
        margin={{ xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) }}
        marginRight={{ xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) }}
      >
        <NoContentCard variant="error" icon="error" text="Error Occured" />
      </Box>
    );
  }

  if (loading === 'loading') {
    return <Loader />;
  }

  if (!resourceId || !activeResourceContent) {
    return (
      <Box>
        <NoContentCard variant="info" icon="cards" text="No Data available" />
      </Box>
    );
  }

  return (
    <Box sx={styles.resourcePage}>
      {/* A practical gives the editor the whole page - the heading would only
          eat height the simulator needs. */}
      {pageNumber == 0 && !isPracticalResource && (
        <InPageHeader title={renderHeading()} />
      )}
      <ResourcePageContent
        key={editorReloadKey}
        disableDownload={!allowResourceDownload}
        isProjectAssessmentEnabled={isProjectAssessmentEnabled}
        pageContents={pageContents}
        pageNumber={pageNumber}
        contentIdAttempt={contentIdAttempt}
        handleAnswerSubmit={handleAnswerSubmit}
        selected_resource_info={selected_resource_info}
        showSubmitButton={true}
        hideAnswerAnimation={demoResource === 'true'}
        eventCollection={{
          answerSelection: async () => {
            await interactionEvent({
              url: '',
              context: 'question',
              name: 'ANSWER_SELECTION',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          },
          submitAnswer: async () => {
            await interactionEvent({
              url: '',
              context: 'question',
              name: 'SUBMIT',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          },
        }}
        resource_raw_data={rawResourceContent}
        practical_project={savedPracticalProject}
        practical_meta={practicalMeta}
        //~ The student is the one host that can persist the work, so it is the
        practicalReadOnly={isProjectSubmitted || isReviewMode || writesBlocked}
        practicalSeedFromAuthoredProject={false}
        practical_resource_menu={
          isPracticalResource && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: pxToRem(8), lg: pxTovW(10) },
              }}
            >
              {!isReviewMode && demoResource !== 'true' && (
                <Tooltip title="Reload">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onReloadClick}
                    aria-label="Reload"
                  >
                    <RefreshIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
              <ResourceListDropdown
                resources={projectResourceLinks}
                currentResourceId={resourceId}
              />
              {codeConflict && (
                <Typography variant="bodyText" color="error">
                  A teammate saved newer code. Your latest changes are not
                  saved. Reload to refresh.
                </Typography>
              )}
            </Box>
          )
        }
        //~ The frame pushes its project whenever the student saves inside the
        //~ editor. That is a deliberate save, so it is persisted right away
        //~ rather than waiting for them to page forward or leave.
        onPracticalProject={(project) => {
          practicalProjectRef.current = project;
          savePracticalProject();
        }}
        onPracticalDirtyChange={(dirty) => {
          practicalDirtyRef.current = dirty;
        }}
        onPracticalError={(error) => {
          console.error(error);
          dispatch(
            setToastInfo({
              label: `Simulator error! ${error.message}`,
              variant: 'error',
              open: true,
            })
          );
        }}
      />
      {!isStandalone && (
        <Box sx={styles.paginationButtonBox}>
          <PaginationButton
            currentPage={pageNumber + 1}
            totalPages={pageContents.length}
            onNextClick={onNextClick}
            onPreviousClick={onPreviousClick}
            disabledNext={isSubmitting}
            disabledPrevious={isSubmitting}
          />
        </Box>
      )}
      <ActionsPopup
        open={!!pendingLeave}
        fontSmall
        iconName="error"
        popupText={
          codeConflict
            ? "Your code wasn't saved because a teammate changed it. Leave anyway?"
            : 'You have unsaved changes. They will be lost if you leave.'
        }
        yesText="Leave"
        noText="Stay"
        yesClickHandler={leaveWithoutSaving}
        noClickHandler={() => setPendingLeave(undefined)}
        handleClose={() => setPendingLeave(undefined)}
      />
      <ActionsPopup
        open={confirmReload}
        fontSmall
        iconName="error"
        popupText="You have unsaved changes. They will be lost if you leave."
        yesText="Reload"
        noText="Cancel"
        yesClickHandler={reloadPracticalProject}
        noClickHandler={() => setConfirmReload(false)}
        handleClose={() => setConfirmReload(false)}
      />
    </Box>
  );
}

interface IBox {
  iconName: string;
  text?: string;
  index: number;
}

const IconTextBox = ({ iconName, text, index }: IBox) => {
  const { isIpadOnly } = useGlobalContext();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: pxToRem(4), md: pxToRem(4), lg: pxTovW(4) },
        alignItems: 'center',
      }}
    >
      <IconWrapper
        name={iconName}
        size={isIpadOnly ? 'large' : 'md'}
        parentFolder="icons"
        type="png"
      />
      <Typography
        variant="bodyText"
        fontWeight={'bold'}
        color={index % 2 === 0 ? 'primary' : 'secondary'}
      >
        {text}
      </Typography>
    </Box>
  );
};
